<template>
  <div class="widget-show">
    <div class="main-content">
      <div class="top-actions">
        <BreadcrumbNavConnected class="breadcrumb"/>
        <button v-if="webhook.id" @click="deleteWebhook" class="delete-widget">
          <svg>
            <use xlink:href="#icon-trash"></use>
          </svg>
          <span>{{ $t("Delete Webhook") }}</span>
        </button>
      </div>

      <ContentBlock :required="true">
        <div slot="instructions">
          <h5>{{ $t('Webhook Details') }}</h5>
        </div>
        <div class="details">
          <div class="grid-one">
            <UISelect :title="$t('Type')" v-model="webhook.event_type">
              <option
                v-for="type in event_types"
                :key="type.id"
                :value="type.id"
              >{{ type.title }}</option>
            </UISelect>
            <UISelect :title="$t('Location')" v-model="webhook.location_id">
              <option
                v-for="location in allLocations"
                :key="location.id"
                :value="location.id"
              >{{ location.title }}</option>
            </UISelect>
            <UIInput
              :title="$t('URL')"
              :instructions="$t('The URL to which the webhook payloads will be sent')"
              v-model="webhook.url"
              required
            />
            <UISelect :title="$t('Booking source')" v-model="webhook.source">
              <option
                v-for="source in source_list"
                :key="source.id"
                :value="source.id"
              >{{ source.title }}</option>
            </UISelect>
            <UISwitch
              :title="$t('Active')"
              v-model="webhook.is_active"
            />
          </div>
          <br />
        </div>
      </ContentBlock>
    </div>
    <bottom-bar
      @save="storeWebhook"
      :saving="saving"
      :valid="valid"
      :backRouterLink="{ name: 'location-webhooks' }"
    />
  </div>
</template>

<script>
/* eslint-disable camelcase */
import Component, { mixins } from 'vue-class-component';
import ContentBlock from '@/core/shared/components/ContentBlock';
import FocusModeMixin from '@/core/shared/misc/FocusModeMixin';
import BottomBar from '@/core/shared/components/BottomBar';
import { isEqual, isEmpty } from 'lodash-es';
import { Chrome } from 'vue-color';
import BreadcrumbNavConnected from '@/core/shared/components/_connected/BreadcrumbNavConnected';
import UIButton from '@/core/shared/components/ui/UIButton';
import UISwitch from '@/core/shared/components/ui/UISwitch';
import UIInput from '@/core/shared/components/ui/UIInput';
import UIFormItem from '@/core/shared/components/ui/UIFormItem';
import UIFileUpload from '@/core/shared/components/ui/UIFileUpload';
import UIRadioThumbnail from '@/core/shared/components/ui/UIRadioThumbnail';
import UISelect from '@/core/shared/components/ui/UISelect';

@Component({
  props: {
    webhookId: {
      type: String,
      required: true,
    },
    locationId: {
      type: String,
      required: true,
    },
  },
  components: {
    ContentBlock,
    BottomBar,
    Chrome,
    UIButton,
    UISwitch,
    UIInput,
    UIFormItem,
    BreadcrumbNavConnected,
    UIFileUpload,
    UIRadioThumbnail,
    UISelect,
  },
})

export default class WebhookShow extends mixins(FocusModeMixin) {
  defaultWebhook = {
    is_active: true,
    event_type: 'booking.create',
    location_id: this.locationId,
    url: '',
    source: 'all',
  };

  webhookChanges = {};
  saving = false;

  advancedAnalyticsInitialized = false;
  advancedAnalytics = false;

  get source_list () {
    return [
      { id: 'all', title: this.$t('All Sources') },
      { id: 'widget', title: this.$t('Booking widget') },
      { id: 'monitor', title: this.$t('Monitor page') },
    ];
  }

  get event_types () {
    return [
      { id: 'booking.create', title: this.$t('Booking Created') },
      { id: 'booking.update', title: this.$t('Booking Updated') },
      { id: 'booking.delete', title: this.$t('Booking Deleted') },
    ];
  }

  get allLocations () {
    return this.$store.getters.currentOrganization.locations;
  }

  get webhook () {
    return new Proxy({ ...this.webhookInStore, ...this.webhookChanges }, {
      set: (object, property, value) => {
        if (isEqual(this.webhookInStore[property], value)) {
          this.$delete(this.webhookChanges, property, value);
        } else {
          this.$set(this.webhookChanges, property, value);
        }
        return true;
      },
    });
  }

  get webhookInStore () {
    return (this.webhookId === 'new') ? this.defaultWebhook : this.$store.getters['entities/Webhook'](this.webhookId);
  }

  get valid () {
    return !!this.webhook.event_type && !!this.webhook.location_id && !!this.webhook.url;
  }

  async created () {
    if (isEmpty(this.webhookInStore)) await this.$store.dispatch('getLocationWebhook', { webhookId: this.webhookId });
    this.show = true;

    let title;
    if (this.webhookId === 'new') {
      this.webhookChanges = this.defaultWebhook;
      title = this.$t('New Webhook');
    } else {
      this.webhookChanges.id = this.webhook.id;
      title = this.$t('Edit Webhook');
    }

    this.$store.commit('setTitle', title);

    this.setBreadcrumb({
      indexTitle: this.$tc('Webhook', 2),
      editTitle: title,
      indexRouteName: 'location-webhooks',
    });
  }

  async storeWebhook () {
    try {
      await this.$store.dispatch('storeWebhook', this.webhook);
      this.saving = false;
      this.$store.commit('setFlash', {
        message: `${this.$t('Webhook saved')}`,
        type: 'success',
      });
      this.$router.push({ name: 'location-webhooks' });
    } finally {
      this.saving = false;
    }
  }

  deleteWebhook () {
    this.$store.commit('setConfirm', {
      show: true,
      message: this.$t('Are you sure you would like to delete this webhook?'),
      icon: 'icon-trash',
      buttons: [
        {
          name: this.$t('No'),
          type: 'cancel',
        },
        {
          name: this.$t('Yes'),
          action: async () => {
            await this.$store.dispatch('deleteWebhook', this.webhook);
            this.$router.push({ name: 'location-webhooks' });
          },
        },
      ],
    });
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.widget-show {
  & >>> .instructions {
    @media (--desktop) {
      flex: 0 0 20rem;
      margin-right: var(--spacingSm);
    }
  }
  & .helper {
    display: block;
    text-transform: none;
  }
  & .checkbox-label {
    align-items: start;
  }
  & .main-content {
    @apply --container;
  }
  & .breadcrumb {
    margin-right: auto;
  }
  & .top-actions {
    display: flex;
    margin: var(--spacingMd) 0 var(--spacingSm) 0;

    @media (--desktop) {
      margin: var(--spacingMd) 0;
    }

    & .delete-widget {
      @apply --buttonTinyPrimary;

      margin: 0;
      margin-left: auto;
      width: auto;
    }
  }

  & .grid-one {
    display: grid;
    margin-bottom: var(--spacingMd);
    grid-gap: var(--spacingSm);
    align-items: top;

    & .checkbox {
      position: relative;
      top: 3rem;
    }

    & .example-button {
      @apply --buttonPrimary;
      display: block;
      width: 100%;
    }

    @media (--tablet) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (--desktop) {
      grid-gap: var(--spacingMd);
    }
  }

  & .grid-three {
    display: grid;
    margin-bottom: var(--spacingMd);
  }
}

& .image-upload {
  display: grid;
  grid-gap: var(--spacingMd);
  grid-template-columns: repeat(4, 1fr);
}

& .coverimage-form {
  display: grid;
  grid-gap: var(--spacingMd);
  /* grid-template-columns: 1fr; */
  @media (--tablet) {
    grid-template-columns: repeat(3, 1fr);
  }
}
& .select-image {
  @media (--tablet) {
    grid-column: 2 / 5;
  }
}
& .image {
  height: 100px;
}
& .bg-image {
  height: 320px;
  background-size: cover;
}
& .imagegrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: var(--spacingSm);
  grid-template-rows: min-content;
  @media (--tablet) {
    grid-template-columns: repeat(3, 1fr);
  }
  &.grid-four {
    @media (--tablet) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}
& .onecolumn {
  grid-template-columns: 1fr;
}

.dark-mode-box {
  padding-top: 15px;
}
</style>
